import { faStar as faStarOutline } from '@fortawesome/pro-regular-svg-icons';
import {
	faGripVertical,
	faPencil,
	faStar,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	ListConfiguration,
	SortField,
	TableSortOption,
} from '@supply-mover-2-0/core';
import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Icon } from '../../../components/base/Icon';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { mergeSortOptions } from '../list-configuration/sorting/SortDropdown';
import { DragHandle } from '../shared/DraggableList';
import { DualSortableProvider, SortableZone } from '../shared/DualSortableList';

export interface SortTabState {
	hasPendingChanges: boolean;
}

/**
 * Snapshot of the Sort-tab edits ColumnEditorLayout's saveAll folds into the
 * saved-search update payload (as listOverrides.sortOptions). `sortOptions` is
 * null to mean "inherit" (no curation — clear any stored override), or an
 * ordered array to mean "use exactly these".
 */
export interface SortTabPendingChanges {
	sortOptions: TableSortOption[] | null;
}

export interface SortTabHandle {
	getPendingChanges: () => SortTabPendingChanges | null;
	reset: () => void;
}

export interface SortTabContentProps {
	/** Working list config (shown columns, defaultSortId, any existing sortOptions). */
	listConfig: ListConfiguration;
	configuration: ListBuilderConfiguration | undefined;
	/**
	 * The base list's full column set — used to offer sorts for columns that
	 * aren't currently displayed (so an admin can add a sort without showing
	 * its column).
	 */
	baseColumns: Array<ColumnConfiguration>;
	/** Raw saved-search overrides — listOverrides.sortOptions seeds working state. */
	savedSearchOverrides: {
		listOverrides: Record<string, unknown> | null;
	} | null;
	/** Current working default sort id (owned by ColumnEditorLayout). */
	defaultSortId: string | undefined;
	/** Update the working default sort id (persists via the saved search's sortId). */
	onSetDefault: (sortId: string | undefined) => void;
	isReadOnly: boolean;
	onStateChange: (state: SortTabState) => void;
}

interface ColumnSortInput {
	columnId: string;
	columnLabel: string;
	headers: NonNullable<ColumnConfiguration['headers']>;
	sortFields: SortField[];
}

/** Build mergeSortOptions' columnSorts input from a column set. */
function toColumnSorts(
	columns: Array<ColumnConfiguration>,
): Array<ColumnSortInput> {
	return columns
		.filter((col) => col.sortable === true && (col.sortFields?.length ?? 0) > 0)
		.map((col) => ({
			columnId: col.id,
			columnLabel: col.headers?.[0]?.text ?? '',
			headers: col.headers ?? [],
			sortFields: col.sortFields ?? [],
		}));
}

/** Two ordered option lists are equal when ids, order, and labels all match. */
function sameOptions(
	a: Array<TableSortOption>,
	b: Array<TableSortOption>,
): boolean {
	const key = (list: Array<TableSortOption>): string =>
		list.map((opt) => `${opt.id} ${opt.name}`).join('');
	return key(a) === key(b);
}

/**
 * Sort tab for the List Configuration modal. Mirrors the Filters tab's
 * selected/available dual-list drag-and-drop:
 *   - "Sort options shown in the dropdown" (selected) — ordered, reorderable,
 *     each renameable, with a star to mark the default.
 *   - "Available sort options" (pool) — every sort derivable from the list's
 *     columns (shown OR hidden) plus configuration.tableSorts, minus what's
 *     already selected. Drag one up to add it.
 *
 * When the selected list matches the auto-derived baseline exactly, no override
 * is stored (the list keeps inheriting the default behavior).
 */
export const SortTabContent = forwardRef<SortTabHandle, SortTabContentProps>(
	(
		{
			listConfig,
			configuration,
			baseColumns,
			savedSearchOverrides,
			defaultSortId,
			onSetDefault,
			isReadOnly,
			onStateChange,
		},
		ref,
	) => {
		const { t } = useTranslation();
		const colors = useThemeColors();

		const tableSorts = useMemo(
			() => configuration?.tableSorts ?? [],
			[configuration?.tableSorts],
		);

		// The sorts the dropdown would auto-derive from the SHOWN columns — the
		// inherited baseline. Used both as the initial selection (faithful to
		// current behavior) and as the "no override needed" comparison target.
		const baselineOptions = useMemo<Array<TableSortOption>>(
			() =>
				mergeSortOptions(
					tableSorts,
					toColumnSorts(listConfig.columns ?? []),
					listConfig.defaultSortId,
				),
			[tableSorts, listConfig.columns, listConfig.defaultSortId],
		);

		// Every sort derivable from ANY column (shown or hidden) plus tableSorts —
		// the full pool the admin can pull from. Working-column edits win over the
		// base column's sort fields.
		const fullPool = useMemo<Array<TableSortOption>>(() => {
			const byId = new Map<string, ColumnConfiguration>();
			for (const col of baseColumns) {
				byId.set(col.id, col);
			}
			for (const col of listConfig.columns ?? []) {
				byId.set(col.id, col);
			}
			return mergeSortOptions(
				tableSorts,
				toColumnSorts([...byId.values()]),
				listConfig.defaultSortId,
			);
		}, [tableSorts, baseColumns, listConfig.columns, listConfig.defaultSortId]);

		// Resolved initial selection: stored override wins, else the list's own
		// sortOptions (code-defined default lists), else the auto-derived baseline.
		const resolvedSelected = useMemo<Array<TableSortOption>>(() => {
			const override = savedSearchOverrides?.listOverrides?.['sortOptions'];
			if (Array.isArray(override) && override.length > 0) {
				return override as Array<TableSortOption>;
			}
			if (listConfig.sortOptions != null && listConfig.sortOptions.length > 0) {
				return listConfig.sortOptions;
			}
			return baselineOptions;
		}, [savedSearchOverrides, listConfig.sortOptions, baselineOptions]);

		const [selected, setSelected] =
			useState<Array<TableSortOption>>(resolvedSelected);
		const [isDirty, setIsDirty] = useState(false);
		const [editingId, setEditingId] = useState<string | null>(null);
		const [editingName, setEditingName] = useState('');

		// Re-seed only when the user selects a different list in the sidebar (same
		// guard as FiltersTabContent — workingListConfig is a new object each render).
		const previousListIdRef = useRef<string | null>(null);
		useEffect(() => {
			if (previousListIdRef.current === listConfig.id) {
				return;
			}
			previousListIdRef.current = listConfig.id;
			setSelected(resolvedSelected);
			setIsDirty(false);
			setEditingId(null);
		}, [listConfig.id, resolvedSelected]);

		useEffect(() => {
			onStateChange({ hasPendingChanges: isDirty });
		}, [isDirty, onStateChange]);

		const selectedIds = useMemo(
			() => new Set(selected.map((opt) => opt.id)),
			[selected],
		);

		const available = useMemo<Array<TableSortOption>>(
			() =>
				fullPool
					.filter((opt) => !selectedIds.has(opt.id))
					.sort((a, b) => a.name.localeCompare(b.name)),
			[fullPool, selectedIds],
		);

		const markDirty = (): void => {
			setIsDirty(true);
		};

		// Insert a sort at a position. Doubles as add (from the available pool) and
		// reorder (within the selected list): the existing entry is removed first,
		// then re-inserted. `targetIndex` is computed against the rendered rows
		// (which still include the dragged row), so when an already-selected item
		// moves DOWN we shift the index back by one to land where the cursor is.
		const addAtPosition = (sortId: string, targetIndex: number): void => {
			if (isReadOnly) {
				return;
			}
			const option = fullPool.find((opt) => opt.id === sortId);
			if (option == null) {
				return;
			}
			setSelected((prev) => {
				const fromIndex = prev.findIndex((opt) => opt.id === sortId);
				const without = prev.filter((opt) => opt.id !== sortId);
				let index = targetIndex;
				if (fromIndex !== -1 && fromIndex < targetIndex) {
					index -= 1;
				}
				index = Math.max(0, Math.min(index, without.length));
				const next = [...without];
				next.splice(index, 0, option);
				return next;
			});
			markDirty();
		};

		const addToEnd = (sortId: string): void => {
			addAtPosition(sortId, selected.length);
		};

		const remove = (sortId: string): void => {
			if (isReadOnly) {
				return;
			}
			setSelected((prev) => prev.filter((opt) => opt.id !== sortId));
			// If the removed option was the default, clear the default so we don't
			// point at a sort that's no longer offered.
			if (defaultSortId === sortId) {
				onSetDefault(undefined);
			}
			markDirty();
		};

		const rename = (sortId: string, name: string): void => {
			if (isReadOnly) {
				return;
			}
			const trimmed = name.trim();
			if (trimmed.length === 0) {
				return;
			}
			setSelected((prev) =>
				prev.map((opt) =>
					opt.id === sortId ? { ...opt, name: trimmed } : opt,
				),
			);
			markDirty();
		};

		const setDefault = (sortId: string): void => {
			if (isReadOnly) {
				return;
			}
			onSetDefault(defaultSortId === sortId ? undefined : sortId);
		};

		useImperativeHandle(ref, () => ({
			getPendingChanges: (): SortTabPendingChanges | null => {
				if (!isDirty) {
					return null;
				}
				// Storing the curated list verbatim, unless it matches the inherited
				// baseline exactly — then store null so the list keeps inheriting the
				// default behavior and storage stays clean.
				return {
					sortOptions: sameOptions(selected, baselineOptions) ? null : selected,
				};
			},
			reset: (): void => {
				setSelected(resolvedSelected);
				setIsDirty(false);
				setEditingId(null);
			},
		}));

		return (
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{isReadOnly && (
					<div
						className={cn(
							'px-3 py-2 rounded border text-sm',
							colors.classes.borderDefault,
							'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
						)}
					>
						{t(
							'This is a read-only list. Use "Copy List" in the footer to create an editable copy.',
						)}
					</div>
				)}

				<DualSortableProvider
					selected={selected}
					keyExtractor={(opt) => opt.id}
					availableIds={available.map((opt) => opt.id)}
					onReorder={(next) => {
						if (isReadOnly) {
							return;
						}
						setSelected(next);
						markDirty();
					}}
					onAdd={(sortId, index) => {
						addAtPosition(sortId, index);
					}}
					onRemove={(sortId) => {
						remove(sortId);
					}}
					disabled={isReadOnly}
					renderOverlay={(id) => {
						const opt = [...selected, ...available].find((o) => o.id === id);
						if (opt == null) {
							return null;
						}
						return (
							<div
								className={cn(
									'flex items-center gap-2 px-2 py-1.5 rounded border shadow-lg',
									colors.classes.borderDefault,
									colors.classes.bgPrimary,
								)}
							>
								<Icon
									icon={faGripVertical}
									size="xs"
									className={cn('shrink-0', colors.classes.textMuted)}
								/>
								<span className={cn('text-sm', colors.classes.textPrimary)}>
									{t(opt.name)}
								</span>
							</div>
						);
					}}
				>
					{/* Selected sort options — shown in the dropdown, in this order */}
					<section
						className={cn(
							'rounded-md border p-3',
							colors.classes.borderDefault,
							colors.classes.bgPrimary,
						)}
					>
						<header className="flex items-center justify-between mb-2">
							<h3
								className={cn(
									'text-sm font-semibold',
									colors.classes.textPrimary,
								)}
							>
								{t(`Sort options in the dropdown (${String(selected.length)})`)}
							</h3>
							<p className={cn('text-xs', colors.classes.textMuted)}>
								{t(
									'Drag a row to reorder. Star one to make it the default. Drag a row into the available pool to remove it.',
								)}
							</p>
						</header>

						<SortableZone
							container="selected"
							items={selected}
							keyExtractor={(opt) => opt.id}
							className="space-y-2 min-h-[2.5rem]"
							emptyContent={
								<div
									className={cn(
										'rounded border-2 border-dashed py-6 text-center text-xs',
										colors.classes.borderDefault,
										colors.classes.textMuted,
									)}
								>
									{t('Drag a sort option here from the available pool below.')}
								</div>
							}
							renderItem={(opt, isDraggingItem) => (
								<SelectedSortRow
									option={opt}
									isDefault={defaultSortId === opt.id}
									isEditing={editingId === opt.id}
									editingName={editingName}
									onEditingNameChange={setEditingName}
									onStartEdit={() => {
										setEditingId(opt.id);
										setEditingName(opt.name);
									}}
									onCommitEdit={() => {
										rename(opt.id, editingName);
										setEditingId(null);
									}}
									onCancelEdit={() => {
										setEditingId(null);
									}}
									onToggleDefault={() => {
										setDefault(opt.id);
									}}
									onRemove={() => {
										remove(opt.id);
									}}
									disabled={isReadOnly}
									isDragging={isDraggingItem}
								/>
							)}
						/>
					</section>

					{/* Available sort options — drag into the list above to add;
					    drop a selected row here to remove it. */}
					<section
						className={cn(
							'rounded-md border p-3',
							colors.classes.borderDefault,
							colors.classes.bgPrimary,
						)}
					>
						<header className="mb-2">
							<h3
								className={cn(
									'text-sm font-semibold',
									colors.classes.textPrimary,
								)}
							>
								{t(`Available sort options (${String(available.length)})`)}
							</h3>
							<p className={cn('text-xs', colors.classes.textMuted)}>
								{t(
									'Sorts derivable from this list’s columns (shown or hidden) and shared table sorts. Drag one up to add it — or drag a selected row here to remove it.',
								)}
							</p>
						</header>

						<SortableZone
							container="available"
							items={available}
							keyExtractor={(opt) => opt.id}
							className="space-y-1 max-h-72 overflow-y-auto min-h-[2.5rem]"
							emptyContent={
								<div
									className={cn(
										'rounded border-2 border-dashed py-6 text-center text-xs',
										colors.classes.borderDefault,
										colors.classes.textMuted,
									)}
								>
									{t(
										'Drag a selected row here to remove it from the dropdown.',
									)}
								</div>
							}
							renderItem={(opt) => (
								<AvailableSortItem
									option={opt}
									disabled={isReadOnly}
									onAdd={() => {
										addToEnd(opt.id);
									}}
								/>
							)}
						/>
					</section>
				</DualSortableProvider>
			</div>
		);
	},
);

SortTabContent.displayName = 'SortTabContent';

// ── Selected row ─────────────────────────────────────

interface SelectedSortRowProps {
	option: TableSortOption;
	isDefault: boolean;
	isEditing: boolean;
	editingName: string;
	onEditingNameChange: (value: string) => void;
	onStartEdit: () => void;
	onCommitEdit: () => void;
	onCancelEdit: () => void;
	onToggleDefault: () => void;
	onRemove: () => void;
	disabled: boolean;
	isDragging: boolean;
}

function SelectedSortRow({
	option,
	isDefault,
	isEditing,
	editingName,
	onEditingNameChange,
	onStartEdit,
	onCommitEdit,
	onCancelEdit,
	onToggleDefault,
	onRemove,
	disabled,
	isDragging,
}: SelectedSortRowProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	return (
		<div
			className={cn(
				'flex items-center gap-2 px-2 py-1.5 rounded border',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				isDragging && 'opacity-60',
			)}
		>
			{!disabled && <DragHandle className="shrink-0" />}

			<button
				type="button"
				disabled={disabled}
				onClick={onToggleDefault}
				title={isDefault ? t('Default sort') : t('Set as default sort')}
				className={cn(
					'shrink-0',
					isDefault ? 'text-amber-500' : colors.classes.textMuted,
					!disabled && 'hover:text-amber-500',
					disabled && 'cursor-not-allowed',
				)}
			>
				<Icon icon={isDefault ? faStar : faStarOutline} size="sm" />
			</button>

			{isEditing ? (
				<input
					type="text"
					value={editingName}
					autoFocus
					onChange={(e) => {
						onEditingNameChange(e.target.value);
					}}
					onBlur={onCommitEdit}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							onCommitEdit();
						} else if (e.key === 'Escape') {
							onCancelEdit();
						}
					}}
					className={cn(
						'flex-1 px-2 py-0.5 text-sm border rounded',
						colors.classes.borderDefault,
						colors.classes.bgPrimary,
						colors.classes.textPrimary,
						'focus:outline-none focus:ring-2 focus:ring-primary-500',
					)}
				/>
			) : (
				<span
					className={cn('flex-1 text-sm truncate', colors.classes.textPrimary)}
				>
					{t(option.name)}
					{isDefault && (
						<span
							className={cn(
								'ml-2 text-xs px-1.5 py-0.5 rounded',
								'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
							)}
						>
							{t('Default')}
						</span>
					)}
				</span>
			)}

			{!disabled && !isEditing && (
				<button
					type="button"
					onClick={onStartEdit}
					title={t('Rename')}
					className={cn(
						'shrink-0 p-0.5 rounded',
						colors.classes.textMuted,
						'hover:text-primary-600 dark:hover:text-primary-400',
					)}
				>
					<Icon icon={faPencil} size="xs" />
				</button>
			)}

			{!disabled && (
				<button
					type="button"
					onClick={onRemove}
					title={t('Remove from dropdown')}
					className={cn(
						'shrink-0 p-0.5 rounded',
						colors.classes.textMuted,
						'hover:text-red-600 dark:hover:text-red-400',
					)}
				>
					<Icon icon={faXmark} size="sm" />
				</button>
			)}
		</div>
	);
}

// ── Available row ────────────────────────────────────

interface AvailableSortItemProps {
	option: TableSortOption;
	disabled: boolean;
	onAdd: () => void;
}

function AvailableSortItem({
	option,
	disabled,
	onAdd,
}: AvailableSortItemProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	return (
		<div
			onDoubleClick={() => {
				if (!disabled) {
					onAdd();
				}
			}}
			className={cn(
				'flex items-center gap-2 px-2 py-1.5 rounded border select-none',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				!disabled && `hover:${colors.classes.bgSecondary}`,
				disabled && 'opacity-50',
			)}
		>
			{!disabled && <DragHandle className="shrink-0" />}
			<span
				className={cn('text-sm flex-1 truncate', colors.classes.textPrimary)}
			>
				{t(option.name)}
			</span>
		</div>
	);
}
